import { Controller } from '@hotwired/stimulus'
import { FloatingLogosConfig } from '../floating_logos_config'
import { shuffle } from '../utilities'
import PerlinNoise from '../perlin_noise'

// Wrapper element which handles position/transform/opacity
const BUBBLE_WRAPPER_CLASSES = ['absolute', 'group']

// Inner element which houses the background sprite and logo class
const BUBBLE_INNER_CLASSES = [
  'bg-white',
  'bg-[url(/images/third-party-logos/integrations/integrations-sprite-sheet.svg)]',
  'bg-no-repeat',
  'rounded-full',
  'shadow-2xl',
  'size-38',
  'bubble-inner'
]

export default class extends Controller {
  static targets = ['logoCtr']
  static values = { integrations: Array, imageDimensions: Array }

  connect() {
    const [imageWidth, imageHeight] = this.imageDimensionsValue
    // we divide by 32 instead of 16 to get the rem value due to 2x retina
    // screens
    this.backgroundSize = `${imageWidth / 32}rem ${imageHeight / 32}rem`
    this.boundStep = this.#step.bind(this)
    this.bubbles = FloatingLogosConfig.bubbles
    this.logos = []

    this.integrationsValue.forEach((id, index) => {
      this.logos[index] = { cssClass: `logo--${id}` }
    })

    this.logos = shuffle(this.logos)

    if (!(this.logos.length > this.bubbles.length + 1)) {
      console.error('Logos array must have at least one more item than bubbles')
    }

    this.containerHeight = FloatingLogosConfig.containerHeight
    this.containerWidth = FloatingLogosConfig.containerWidth
    this.introDelay = FloatingLogosConfig.introDelay
    this.introDuration = FloatingLogosConfig.introDuration
    this.maxShrink = FloatingLogosConfig.maxShrink
    this.noiseScale = FloatingLogosConfig.noiseScale
    this.noiseSpeed = FloatingLogosConfig.noiseSpeed
    this.scrollSpeed = FloatingLogosConfig.scrollSpeed
    this.noiseT = 0
    this.scrollX = 0
    this.firstTick = null
    this.lastTick = 0
    this.vertShink = 0
    this.perlin = new PerlinNoise()
    this.verticalShrink()
    this.#initBubbles()
    this.boundStep(0)
  }

  verticalShrink() {
    const vertShrink = this.#calcVertShrink(1e3, 800, window.innerHeight)
    this.vertShrink = this.#minMax(vertShrink, 0, 1)
  }

  #adjustForWindowHeight(y) {
    const shrink = this.vertShrink * this.maxShrink
    return y * (1 - shrink) + (this.containerHeight / 2) * shrink
  }

  #calcVertShrink(e, n, windowHeight) {
    return (windowHeight - e) / (n - e)
  }

  #createBubbleEl() {
    const bubble = document.createElement('bubble')
    // Apply wrapper classes to the outer element
    bubble.classList.add(...BUBBLE_WRAPPER_CLASSES)

    // Create inner element to hold background image and logo-specific class
    const inner = document.createElement('div')
    inner.classList.add(...BUBBLE_INNER_CLASSES)
    inner.style.backgroundSize = this.backgroundSize

    bubble.appendChild(inner)
    this.logoCtrTarget.appendChild(bubble)
    return bubble
  }

  #getLogo(el) {
    return { cssClass: el.dataset.logo }
  }

  #initBubbles() {
    this.bubbles.forEach(bubble => {
      bubble.seedX = 1e4 * Math.random()
      bubble.seedY = 1e4 * Math.random()
      bubble.noiseX = bubble.noiseY = 0
      bubble.introDelay = Math.random() * this.introDelay
      bubble.introProgress = 0
      if (!bubble.el) bubble.el = this.#createBubbleEl()
      this.#updateBubble(bubble)
      const logo = this.#randomLogo()
      this.#setLogo(bubble.el, logo)
    })
  }

  #introProgressFunc(e) {
    return e < 0.5 ? 2 * e * e : (4 - 2 * e) * e - 1
  }

  #minMax(a, min, max) {
    return Math.max(Math.min(a, max), min)
  }

  #randomLogo(logo = undefined) {
    if (logo) this.logos.push({ cssClass: logo.cssClass })
    return this.logos.shift()
  }

  #setLogo(el, logo) {
    // Apply/remove logo class on the inner element while keeping dataset on wrapper
    const inner = el.querySelector('.bubble-inner') || el
    const existingClass = el.dataset.logo
    if (existingClass) inner.classList.remove(existingClass)
    inner.classList.add(logo.cssClass)
    el.dataset.logo = logo.cssClass
  }

  #step(tick) {
    if (!this.firstTick) this.firstTick = tick
    tick -= this.firstTick
    this.tickLength = tick - this.lastTick
    this.lastTick = tick
    this.scrollX -= this.tickLength * this.scrollSpeed
    this.noiseT += this.tickLength * this.noiseSpeed

    this.bubbles.forEach(bubble => {
      bubble.noiseX =
        this.perlin.getVal(bubble.seedX + this.noiseT) * this.noiseScale -
        this.noiseScale / 2

      bubble.noiseY =
        this.perlin.getVal(bubble.seedY + this.noiseT) * this.noiseScale -
        this.noiseScale / 2

      if (tick > bubble.introDelay && bubble.introProgress < 1) {
        const progress =
          bubble.introProgress + this.tickLength / this.introDuration

        bubble.introProgress = Math.min(1, progress)
      }

      this.#updateBubble(bubble)
    })

    window.requestAnimationFrame(this.boundStep)
  }

  #updateBubble(b) {
    const x = b.x + b.noiseX + this.scrollX
    let y = b.y + b.noiseY
    y = this.#adjustForWindowHeight(y)

    if (x < -200) {
      b.x += this.containerWidth
      const newLogo = this.#randomLogo(this.#getLogo(b.el))
      this.#setLogo(b.el, newLogo)
    }

    let s = b.s || 1
    s *= this.#introProgressFunc(b.introProgress) / 20 + 0.95
    b.el.style.opacity = this.#introProgressFunc(b.introProgress)
    b.el.style.transform = `translate(${x}px, ${y}px) scale(${s})`
  }
}
